import { clsx } from '@alexis/helpers/clsx';
import { dateApiFormat } from '@alexis/helpers/date';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { localStorageGet, localStorageSave } from '@alexis/helpers/localStorage';
import { translateText } from '@alexis/helpers/translation';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { catchError, defer, expand, of, takeWhile } from 'rxjs';

import { getTopHotels } from '@apis/hotel';

import Button from '@components/Button';
import Carousel from '@components/Carousel';
import HotelMiniCard from '@components/hotels/HotelMiniCard';

import {
  FLIGHT_RECENT_SEARCH_LOCAL_STORAGE_KEY,
  HOTEL_CROSS_SELLING_POPUP_COUNT_LOCAL_STORAGE_KEY,
} from '@constants/localStorage';

import { GlobalContext } from '@context/GlobalContext';

import { convertGuestRoomsToSearchParam } from '@helpers/hotel';
import { hotelsRoute } from '@helpers/routeTranslation';

import CloseIcon from '@icons/close.svg';

import {
  getCurrencyState,
  getCurrentSiteState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { CurrentSite } from '@wegoTypes/currentSite';
import { FlightSearch } from '@wegoTypes/flights/flightSearch';
import { MetaSearchHotelPopular } from '@wegoTypes/hotels/metasearch/metaSearchHotelPopular';
import { SimilarHotelViewModel } from '@wegoTypes/hotels/similarHotelViewModel';

import styles from './HotelCrossSell.module.scss';

interface HotelCrossSellProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  pageViewId: string;
  wegoAnalyticsClientId: string;
  wegoAnalyticsClientSessionId: string;
  className?: string;
}

const now = dateApiFormat(new Date());

const HotelCrossSell: React.FC<HotelCrossSellProps> = ({
  isOpen,
  setIsOpen,
  pageViewId,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  className,
}): JSX.Element => {
  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const apiBaseUrl = API_BASE_URL;
  const currency: Currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const user = useSelector(getUserState);

  const [topHotels, setTopHotels] = useState<MetaSearchHotelPopular>();
  const eventData = {
    id: pageViewId,
    category: 'cross_selling_hotels_popup',
    object: 'cross_selling_hotels_popup',
    action: 'click',
  };
  const baseline =
    userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === 'hmeta508_cross_selling_hotels_popup_cta_buttons',
    )?.value === '0';
  const isCrossSellPopupEnabled =
    userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === 'w_hmeta730_cross_sell_popup_in_fmeta',
    )?.value === '1';

  const flightRecentSearch = localStorageGet<FlightSearch>(FLIGHT_RECENT_SEARCH_LOCAL_STORAGE_KEY);
  const prevCount =
    localStorageGet<Record<string, number>>(HOTEL_CROSS_SELLING_POPUP_COUNT_LOCAL_STORAGE_KEY)?.[
      now
    ] || 0;

  const searchInfo = useMemo(() => {
    if (!flightRecentSearch) return;
    const legSearch = flightRecentSearch.legSearches[0];
    const checkInDate = dateApiFormat(new Date(legSearch.outboundDateMilliseconds!));
    const checkOutDate = dateApiFormat(new Date(legSearch.inboundDateMilliseconds!));
    const cityCode =
      legSearch.inboundPlace !== 'Anywhere'
        ? legSearch.inboundPlace?.cityCode
        : legSearch.inboundPlace;
    const cityName =
      legSearch.inboundPlace !== 'Anywhere'
        ? legSearch.inboundPlace?.cityName
        : legSearch.inboundPlace;
    const childrenAges = [];

    if (flightRecentSearch?.infantCount) {
      childrenAges.push(...Array(flightRecentSearch?.infantCount).fill(1));
    }
    if (flightRecentSearch?.childCount) {
      childrenAges.push(...Array(flightRecentSearch?.childCount).fill(12));
    }

    return {
      checkInDate,
      checkOutDate,
      cityCode,
      cityName,
      guestRooms: [
        {
          adultsCount: flightRecentSearch.adultCount,
          childrenCount: childrenAges.length,
          childrenAges,
        },
      ],
    };
  }, [JSON.stringify(flightRecentSearch?.legSearches)]);

  const mappedHotelList = useMemo(() => {
    return (
      topHotels?.hotels.map((hotel) => {
        const rate = topHotels.rates.find((rate) => rate.hotelId === hotel.id)!;
        const district = topHotels.districts.find((district) => district.id === hotel.districtId)!;

        return {
          hotel: {
            id: hotel.id,
            name: hotel.name,
            star: hotel.star!,
            districtName: district?.name || '',
            bestRate: { price: rate?.price || {} },
            reviews: hotel.reviews,
            images: hotel.images,
          },
        };
      }) || []
    );
  }, [topHotels]);

  const guestsSearchParam = searchInfo && convertGuestRoomsToSearchParam(searchInfo.guestRooms);
  const searchResultPathname = searchInfo
    ? `${window.location.origin}${hotelsRoute(currentSite, undefined)}/searches/${
        searchInfo.cityCode
      }/${searchInfo.checkInDate}/${searchInfo.checkOutDate}`
    : '';

  const handleButtonClick = (isCloseButton: boolean) => {
    if (!isCloseButton) {
      if (searchInfo) {
        const guestsSearchParam = convertGuestRoomsToSearchParam(searchInfo.guestRooms);
        const searchResultPathname = searchInfo
          ? `${hotelsRoute(currentSite, undefined)}/searches/${searchInfo.cityCode}/${
              searchInfo.checkInDate
            }/${searchInfo.checkOutDate}`
          : '';

        window.open(
          `${searchResultPathname}?guests=${guestsSearchParam}&wego_growth=true`,
          '_blank',
        );
      }
    } else {
      setIsOpen(false);
      localStorageSave(HOTEL_CROSS_SELLING_POPUP_COUNT_LOCAL_STORAGE_KEY, { [now]: prevCount + 1 });
    }

    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      const genzoData = {
        ...eventData,
        value: isCloseButton ? 'close' : 'find_rooms',
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        genzoData,
      );
    }
  };

  const handleOnClose = () => {
    const genzoData = {
      ...eventData,
      value: 'close',
    };
    genzoTrackActionEvent(
      apiBaseUrl,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      user?.userHash,
      genzoData,
    );
    setIsOpen(false);
    localStorageSave(HOTEL_CROSS_SELLING_POPUP_COUNT_LOCAL_STORAGE_KEY, { [now]: prevCount + 1 });
  };

  const handleCardClick = (hotel: SimilarHotelViewModel) => {
    const genzoData = {
      ...eventData,
      value: hotel.hotel.id.toString(),
    };
    genzoTrackActionEvent(
      apiBaseUrl,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      user?.userHash,
      genzoData,
    );
  };

  // Trigger popular hotels metasearch.
  useEffect(() => {
    if (
      flightRecentSearch &&
      flightRecentSearch.searchType === 'roundTrip' &&
      searchInfo &&
      prevCount < 2 &&
      isCrossSellPopupEnabled
    ) {
      const topHotels = defer(() =>
        getTopHotels(
          apiBaseUrl,
          {
            siteCode: currentSite.countryCode,
            locale,
            currencyCode: currency.code,
            checkIn: searchInfo.checkInDate!,
            checkOut: searchInfo.checkOutDate!,
            room: searchInfo?.guestRooms[0],
            cityCode: searchInfo.cityCode?.toUpperCase(),
          },
          user,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          { wgGrowth: 'dWeb_lightweight' },
        ),
      );

      let hasRetried = false;

      const subscription = topHotels
        .pipe(
          expand((value) => {
            if (!value.done && !hasRetried) {
              hasRetried = true;
              return topHotels;
            }
            return of();
          }),
          takeWhile((value) => !value.done || !hasRetried, true),
          catchError((err) => {
            console.log('error during retry process', err);
            return of(null);
          }),
        )
        .subscribe({
          next: (value): void => {
            setTopHotels(value as MetaSearchHotelPopular);
          },
          error: (err) => {
            console.log('unhandled error', err);
          },
        });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [JSON.stringify(searchInfo), isCrossSellPopupEnabled]);

  return (
    <>
      {flightRecentSearch && isOpen && isCrossSellPopupEnabled && prevCount < 2 ? (
        <div className={clsx(styles.container, className)} data-open={isOpen}>
          <div className={styles.overlay} onClick={handleOnClose}></div>
          <div className={styles.content}>
            <CloseIcon
              className={styles.closeIcon}
              onClick={handleOnClose}
              data-testid='CloseIcon'
            />
            <div className={styles.title}>{translations.exclusive_room_rates}</div>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{
                __html: translateText(
                  translations.hotel_cross_sellings_desc,
                  locale,
                  searchInfo?.cityName || '',
                ),
              }}
            ></div>

            <div className={styles.body}>
              <div className={styles.hotelList}>
                {topHotels && topHotels?.hotels.length > 0 ? (
                  <Carousel
                    className={styles.carousel}
                    isRtl={isRtl}
                    itemLength={topHotels?.hotels.length}
                    itemWidth={226}
                    scrollWidth={224}
                  >
                    <div className={styles.hotelMiniCard}>
                      {mappedHotelList.map((hotel) => {
                        return (
                          <HotelMiniCard
                            href={`${searchResultPathname}/${hotel.hotel.id}?guests=${guestsSearchParam}&wego_growth=true`}
                            key={hotel.hotel.id}
                            similarHotelViewModel={hotel}
                            onCardClick={() => handleCardClick(hotel)}
                          />
                        );
                      })}
                    </div>
                  </Carousel>
                ) : (
                  [...Array(2).keys()].map((value: number) => (
                    <div key={value} className={styles.hotelCardSkeleton}>
                      <div className={styles.imageGallery}></div>

                      <div className={styles.contentWrapper}>
                        <div className={styles.name}></div>
                        <div className={styles.starAndDistrict}>
                          <div className={styles.item}></div>
                          <div className={styles.item}></div>
                        </div>

                        <div className={styles.reviews}>
                          <div className={styles.item}></div>
                          <div className={styles.item}></div>
                          <div className={styles.item}></div>
                        </div>

                        <div className={styles.price}></div>
                        <div className={styles.priceOptions}></div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={styles.buttonGroups}>
                <Button
                  colorScheme='grey'
                  borderWidth='thick'
                  variant='outline'
                  onClick={() => handleButtonClick(baseline)}
                >
                  {baseline ? translations.close : translations.find_rooms}
                </Button>
                <Button onClick={() => handleButtonClick(!baseline)}>
                  {baseline ? translations.find_rooms : translations.close}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HotelCrossSell;
